<script setup>
import FileTextIcon from '@iconify-vue/ph/file-text';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, all = false } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    all: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'tenant.name',
            header: () => 'Client',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.tenant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'name',
            header: () => 'Nom',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.display({
            id: 'details',
            header: 'Détails',
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            props.row.original.description
                                ? h(
                                      DisplayContentAction,
                                      {
                                          title: 'Description',
                                          content:
                                              props.row.original.description,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir la description',
                                                  type: 'primary',
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  )
                                : null,
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'startedAt',
            header: () => 'Date de début',
            cell: (props) =>
                props.row.original.startedAt
                    ? h(TextCell, {
                          text: formatDate(props.row.original.startedAt),
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'endedAt',
            header: () => 'Date de fin',
            cell: (props) =>
                props.row.original.endedAt
                    ? h(TextCell, {
                          text: formatDate(props.row.original.endedAt),
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'createdBy',
            header: () => 'Créé par',
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.createdBy.label,
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            tenant || props.row.original.tenant
                                ? h(ActionButton, {
                                      size: 'small',
                                      square: true,
                                      title: 'Modifier',
                                      href: route(
                                          'front.projectManagement.sem.event',
                                          {
                                              tenant: tenant
                                                  ? tenant.id
                                                  : props.row.original.tenant
                                                        .id,
                                              semEventId: props.row.original.id,
                                          },
                                      ),
                                      icon: PencilSimpleIcon,
                                  })
                                : null,
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.sem.postEvents', {
        tenant: tenant ? tenant.id : null,
        all,
    }),
    columnPinning: {
        right: ['actions'],
    },
    columnVisibility: {
        'tenant.name': !tenant,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                v-if="tenant"
                size="small"
                :href="
                    route('front.projectManagement.sem.event', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
